
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Bot, Loader2, X } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import { initializeOpenAIClient } from '@/lib/openai';
import { getWebsiteAssistantCompletion } from '@/lib/websiteAssistantCompletion';
import { ChatMessage } from '@/types/chat';
import OpenAI from 'openai';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface WebsiteChatModalProps {
  onClose: () => void;
}

interface Message {
  id: string;
  content: string;
  isUser: boolean;
}

const WebsiteChatModal = ({ onClose }: WebsiteChatModalProps) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      content: 'Hola, soy tu ayudante. ¿Cómo puedo ayudarte a usar nuestra página o app?',
      isUser: false,
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [openai, setOpenai] = useState<OpenAI | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const { toast } = useToast();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const initClient = async () => {
      try {
        const client = await initializeOpenAIClient();
        setOpenai(client);
      } catch (error) {
        console.error('Failed to initialize OpenAI client:', error);
        toast({
          title: 'Error',
          description: 'No pudimos conectar con nuestro asistente. Por favor intenta nuevamente más tarde.',
          variant: 'destructive',
        });
      }
    };
    
    initClient();
    
    // Focus the input field when the modal opens
    if (inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [toast, isMobile]);
  
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || !openai) return;
    
    const userMessage = {
      id: Date.now().toString(),
      content: input.trim(),
      isUser: true,
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      const response = await getWebsiteAssistantCompletion(
        openai, 
        userMessage.content,
        conversationHistory
      );
      
      if (response) {
        const botMessage = {
          id: (Date.now() + 1).toString(),
          content: response,
          isUser: false,
        };
        
        setMessages(prev => [...prev, botMessage]);
        
        // Update conversation history for context
        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: userMessage.content } as ChatMessage,
          { role: 'assistant', content: response } as ChatMessage
        ]);
      }
    } catch (error) {
      console.error('Error getting assistant response:', error);
      toast({
        title: 'Error',
        description: 'No pudimos procesar tu pregunta. Por favor intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If mobile, render without card wrapper for drawer
  if (isMobile) {
    return (
      <div className="flex flex-col h-full">
        {/* Chat messages */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto space-y-4 mb-4"
        >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl p-3 text-sm shadow-sm",
                  message.isUser
                    ? "bg-primary text-white rounded-tr-sm"
                    : "bg-gray-100 rounded-tl-sm"
                )}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl p-3 bg-gray-100 shadow-sm rounded-tl-sm">
                <div className="flex space-x-1.5 items-center">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce delay-75"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce delay-150"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Input form */}
        <form onSubmit={handleSubmit} className="flex gap-2 border-t pt-4">
          <Input
            ref={inputRef}
            type="text"
            placeholder="¿Cómo uso esta página?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            className="flex-1 text-sm h-12"
          />
          <Button 
            type="submit" 
            size="icon"
            className="h-12 w-12 bg-primary hover:bg-primary/90"
            disabled={isLoading || !input.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    );
  }
  
  // Desktop version with card
  return (
    <Card className={cn(
      "w-80 h-96 flex flex-col shadow-xl rounded-2xl border",
      "bg-white animate-in fade-in slide-in-from-bottom-4 duration-300 ease-out"
    )}>
      <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between bg-primary/5 rounded-t-2xl">
        <CardTitle className="text-md flex items-center gap-2 font-medium text-navy">
          <div className="bg-primary/10 p-1.5 rounded-full">
            <Bot className="h-4 w-4 text-primary" />
          </div>
          <span>¿Cómo podemos ayudarte?</span>
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 rounded-full hover:bg-primary/10" 
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-hidden p-3">
        <div 
          ref={chatContainerRef}
          className="h-full overflow-y-auto space-y-3 pr-1"
        >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} mb-2`}
            >
              <div
                className={cn(
                  "max-w-[90%] rounded-2xl p-3 text-sm shadow-sm",
                  message.isUser
                    ? "bg-primary text-white rounded-tr-sm"
                    : "bg-gray-100 rounded-tl-sm"
                )}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[90%] rounded-2xl p-3 bg-gray-100 shadow-sm rounded-tl-sm">
                <div className="flex space-x-1.5 items-center">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce delay-75"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce delay-150"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-3 border-t bg-gray-50/50 rounded-b-2xl">
        <form onSubmit={handleSubmit} className="flex w-full gap-2">
          <Input
            ref={inputRef}
            type="text"
            placeholder="¿Cómo uso esta página?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            className="flex-1 text-sm h-10 border-gray-200 focus-visible:ring-primary"
          />
          <Button 
            type="submit" 
            size="icon"
            className="h-10 w-10 bg-primary hover:bg-primary/90"
            disabled={isLoading || !input.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
};

export default WebsiteChatModal;
